import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/api/auth';
import { setAusverkauf, type StockItem } from '@/api/stock';
import { AusverkaufAbzeichen } from '@/components/AusverkaufAbzeichen';

export function AusverkaufSchalter({ item }: { item: StockItem }) {
  const auth = useAuth();
  const qc = useQueryClient();
  const [wahl, setWahl] = useState<string | null>(null);
  const [ladefehler, setLadefehler] = useState<string | null>(null);
  const speichern = useMutation({
    mutationFn: (wert: boolean | null) => setAusverkauf(item.sku, wert),
    onSuccess: async (bestaetigt) => {
      // Die bestätigte Schreibantwort ist sofort gültig, auch wenn ein späteres
      // GET scheitert. Andere Detailfelder bleiben im Cache erhalten.
      qc.setQueryData<StockItem>(['grundartikel', item.sku], (alt) => ({ ...(alt ?? item), ...bestaetigt }));
      const ergebnisse = await Promise.allSettled([
        qc.invalidateQueries({ queryKey: ['grundartikel', item.sku] }, { throwOnError: true }),
        qc.invalidateQueries({ queryKey: ['catalog-grundartikel'] }, { throwOnError: true }),
        qc.invalidateQueries({ queryKey: ['dashboard'] }, { throwOnError: true }),
        qc.invalidateQueries({ queryKey: ['system', 'dashboard'] }, { throwOnError: true }),
        qc.invalidateQueries({ queryKey: ['purchase', 'order'] }, { throwOnError: true }),
        qc.invalidateQueries({ queryKey: ['inventory-stock'] }, { throwOnError: true }),
      ]);
      if (ergebnisse.some((r) => r.status === 'rejected')) {
        setLadefehler('Ausverkauf gespeichert. Nachladen fehlgeschlagen — bestätigter Schalterstand bleibt erhalten. Seite erneut laden.');
      }
      setWahl(null);
    },
    onError: () => setWahl(null),
  });
  return <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
    <AusverkaufAbzeichen {...item} />
    {item.is_grundartikel && <label className="flex items-center gap-2 text-ink-2">
      Ausverkauf
      <select aria-label="Ausverkauf" className="h-8 rounded-md border border-line bg-panel px-2 text-ink disabled:opacity-60"
        disabled={!auth.user?.permissions?.article_general || speichern.isPending}
        value={wahl ?? (item.ausverkauf_override == null ? '' : String(item.ausverkauf_override))}
        onChange={(e) => {
          setLadefehler(null);
          setWahl(e.target.value);
          speichern.mutate(e.target.value === '' ? null : e.target.value === 'true');
        }}>
        <option value="">wie DR</option><option value="true">an</option><option value="false">aus</option>
      </select>
    </label>}
    {speichern.error && <p role="alert" className="w-full text-coral">{speichern.error instanceof Error ? speichern.error.message : 'Ausverkauf konnte nicht gespeichert werden.'}</p>}
    {ladefehler && <p role="alert" className="w-full text-coral">{ladefehler}</p>}
  </div>;
}
